import axios from "axios";
import { useEffect, useState } from "react";
import { base_url } from "../../config";
import { Pencil } from "lucide-react";
import Switch from "@mui/material/Switch";

const Layout = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
    <div
      className="bg-white relative flex flex-col"
      style={{
        width: "80vw",
        maxWidth: "900px",
        height: "80vh",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        overflow: "auto",
      }}
    >
      <button
        className="absolute top-4 right-4 text-2xl font-bold text-gray-700"
        onClick={onClose}
      >
        &times;
      </button>

      {children}
    </div>
  </div>
);
const Loading = ({ onClose }) => (
  <Layout onClose={onClose}>
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-gray-900"></div>
    </div>
  </Layout>
);

const BadgeControl = ({ open, loading, data, onClose, onUpdate }) => {
  const [stdata, setStData] = useState(data);
  const [basic, setBasic] = useState({});
  const [extra, setExtra] = useState({});
  const [user, setUser] = useState({});
  const [appliyingPaperMt, setAppliyingPaperMt] = useState(false);
  const [editingNameBadge, setEditingNameBadge] = useState(false);
  const [editingAffiliationBadge, setEditingAffiliationBadge] = useState(false);
  const [updatingNameBadge, setUpdatingNameBadge] = useState(false);
  const [updatingAffiliationBadge, setUpdatingAffiliationBadge] =
    useState(false);
  const [updatingBadgePrinted, setUpdatingBadgePrinted] = useState(false);
  const [updatingBadgePrintRequest, setUpdatingBadgePrintRequest] =
    useState(false);

  useEffect(() => {
    if (stdata && stdata.data) {
      setBasic(stdata.data);
      setExtra(stdata.data.extra_registration_i_j_c_a_i25 || {});
      setUser(stdata.data.user || {});
    }
  }, [stdata]);

  useEffect(() => {
    setStData(data);
  }, [data]);
  if (!open) return null;
  if (loading) return <Loading onClose={onClose} />;

  const updateNameBadge = async (id, name) => {
    setUpdatingNameBadge(true);
    try {
      const response = await axios.post(
        `${base_url}/api/admin/update_name_badge`,
        {
          id: id,
          name: name,
        }
      );
      setStData(response.data);
    } catch (error) {
      console.error("Error updating name badge:", error);
    } finally {
      setUpdatingNameBadge(false);
      setEditingNameBadge(false);
    }
  };

  const updateAffiliationBadge = async (id, affiliation) => {
    setUpdatingAffiliationBadge(true);
    try {
      const response = await axios.post(
        `${base_url}/api/admin/update_affiliation_badge`,
        {
          id: id,
          affiliation,
        }
      );
      setStData(response.data);
    } catch (error) {
      console.error("Error updating name badge:", error);
    } finally {
      setUpdatingAffiliationBadge(false);
      setEditingAffiliationBadge(false);
    }
  };

  const handleBadgePrint = async () => {
    setAppliyingPaperMt(true);
    try {
      const response = await axios.post(
        `${base_url}/api/admin/print_badge`,
        {
          registration_id: basic.id,
        },
        {
          responseType: "blob", // Important: tell axios to expect a binary response
        }
      );

      // Create a blob URL from the response
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = `badge_${basic.registration_id || basic.id}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error printing badge:", error);
    } finally {
      setAppliyingPaperMt(false);
    }
  };

  return (
    <Layout onClose={onClose}>
      <div className="p-6 flex flex-col h-full  ">
        <h2 className="text-2xl font-bold mb-4">Badge Control</h2>
        <div className="h-full flex flex-col">
          <div
            style={{ flex: "1 1 0" }}
            className="grid grid-cols-1  gap-4 mb-4 "
          >
            <div className="bg-gray-100 p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-2">
                Personal Information
              </h3>
              <p className="text-gray-600">
                Name:{" "}
                <b>
                  {extra.first_name} {extra.family_name}
                </b>
              </p>
              <p className="text-gray-600">
                Affiliation: <b>{extra.affiliation}</b>
              </p>
              {extra.affilition2 && (
                <p className="text-gray-600">
                  Affiliation 2: <b>{extra.affilition2}</b>
                </p>
              )}
              <p className="text-gray-600">
                Email: <b>{user.email}</b>{" "}
              </p>
              <h3 className="text-lg font-semibold mb-2 mt-4">Badge</h3>

              <div className="text-gray-600  flex ">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span>Name in the badge:</span>
                </div>
                {updatingNameBadge && <div className="ml-2">Working...</div>}
                {!updatingNameBadge && !editingNameBadge && (
                  <>
                    <div className="font-bold pl-2">{extra.name_badge}</div>
                    <Pencil
                      size={12}
                      className="h-5 ml-5 cursor-pointer text-black"
                      onClick={() => setEditingNameBadge(true)}
                    />
                  </>
                )}
                {!updatingNameBadge && editingNameBadge && (
                  <>
                    <input
                      type="text"
                      value={extra.name_badge}
                      onChange={(e) =>
                        setExtra({ ...extra, name_badge: e.target.value })
                      }
                      className="border border-gray-300 rounded p-1 mx-2"
                    />
                    <button
                      className="ml-2 border border-gray-300 rounded px-2 py-1"
                      onClick={() =>
                        updateNameBadge(extra.registration_id, extra.name_badge)
                      }
                    >
                      Save
                    </button>
                    <button
                      className="ml-2 border border-gray-300 rounded px-2 py-1"
                      onClick={() => {
                        setEditingNameBadge(false);
                        setExtra({
                          ...extra,
                          name_badge:
                            basic.extra_registration_i_j_c_a_i25.name_badge,
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
              <div className="text-gray-600  flex">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span>Affiliation in the badge:</span>
                </div>
                {updatingAffiliationBadge && (
                  <div className="ml-2">Working...</div>
                )}
                {!updatingAffiliationBadge && !editingAffiliationBadge && (
                  <>
                    <div className="font-bold pl-2">
                      {extra.affiliation_badge}
                    </div>
                    <Pencil
                      size={12}
                      className="h-5 ml-5 cursor-pointer text-black"
                      onClick={() => setEditingAffiliationBadge(true)}
                    />
                  </>
                )}
                {!updatingAffiliationBadge && editingAffiliationBadge && (
                  <>
                    <input
                      type="text"
                      value={extra.affiliation_badge}
                      onChange={(e) =>
                        setExtra({
                          ...extra,
                          affiliation_badge: e.target.value,
                        })
                      }
                      className="border border-gray-300 rounded p-1 mx-2"
                    />
                    <button
                      className="ml-2 border border-gray-300 rounded px-2 py-1"
                      onClick={() =>
                        updateAffiliationBadge(
                          extra.registration_id,
                          extra.affiliation_badge
                        )
                      }
                    >
                      Save
                    </button>
                    <button
                      className="ml-2 border border-gray-300 rounded px-2 py-1"
                      onClick={() => {
                        setEditingAffiliationBadge(false);
                        setExtra({
                          ...extra,
                          affiliation_badge:
                            basic.extra_registration_i_j_c_a_i25
                              .affiliation_badge,
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
              <div className="flex">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2 mt-4">
                    Badge Control
                  </h3>
                  <div className="text-gray-600  flex">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>Badge printed:</span>
                    </div>

                    {updatingBadgePrinted && (
                      <div className="ml-2">Working...</div>
                    )}
                    {!updatingBadgePrinted && (
                      <Switch
                        checked={!!extra.badge_printed}
                        onChange={async (e) => {
                          setUpdatingBadgePrinted(true);
                          try {
                            const response = await axios.post(
                              `${base_url}/api/admin/update_badge_printed`,
                              {
                                id: extra.registration_id,
                                value: e.target.checked,
                              }
                            );
                            setExtra({
                              ...extra,
                              badge_printed: response.data.badge,
                            });
                            onUpdate();
                          } catch (error) {
                            console.error(
                              "Error updating badge printed:",
                              error
                            );
                          } finally {
                            setUpdatingBadgePrinted(false);
                          }
                        }}
                        className="ml-2"
                      />
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2 mt-4">
                    Bulk Print Requests
                  </h3>
                  <div className="text-gray-600  flex">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>Requested:</span>
                    </div>

                    {updatingBadgePrintRequest && (
                      <div className="ml-2">Working...</div>
                    )}
                    {!updatingBadgePrintRequest && (
                      <Switch
                        checked={!!extra.bulk_print_requested}
                        onChange={async (e) => {
                          setUpdatingBadgePrintRequest(true);
                          try {
                            const response = await axios.post(
                              `${base_url}/api/admin/update_badge_print_request`,
                              {
                                id: extra.registration_id,
                                value: e.target.checked,
                              }
                            );
                            setExtra({
                              ...extra,
                              bulk_print_requested: response.data.badge,
                            });
                            onUpdate();
                          } catch (error) {
                            console.error(
                              "Error updating badge print request:",
                              error
                            );
                          } finally {
                            setUpdatingBadgePrintRequest(false);
                          }
                        }}
                        className="ml-2"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: "100px", paddingTop: "20px" }} className="mt-4">
            <button
              onClick={() => handleBadgePrint()}
              disabled={appliyingPaperMt}
              className={` px-4 py-2  bg-blue-500 text-white border rounded hover:bg-blue-300 ${
                appliyingPaperMt ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {appliyingPaperMt ? "Working..." : "Badge"}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BadgeControl;
